/**
 * Role Badge Component
 *
 * A reusable component for displaying workspace role badges with consistent styling.
 * Maps roles to appropriate badge variants for visual hierarchy.
 */

import { Badge, cn } from '@loader/ui';

import { capitalize } from '@/utils/formatters';

export type WorkspaceRole = 'owner' | 'admin' | 'editor' | 'viewer';

interface RoleBadgeProps {
  /**
   * The workspace role to display
   */
  role: WorkspaceRole | string;
  /**
   * Additional CSS classes to apply to the badge
   */
  className?: string;
}

/**
 * Get badge variant for a given role
 *
 * Maps roles to badge variants for visual hierarchy:
 * - owner/admin: default (primary color)
 * - editor: secondary (muted color)
 * - viewer: outline (subtle border)
 *
 * @param role - The workspace role
 * @returns Badge variant
 */
const getRoleBadgeVariant = (
  role: string
): 'default' | 'secondary' | 'outline' => {
  switch (role) {
    case 'owner':
    case 'admin':
      return 'default';
    case 'editor':
      return 'secondary';
    case 'viewer':
    default:
      return 'outline';
  }
};

/**
 * RoleBadge Component
 *
 * Displays a workspace role with appropriate styling based on the role type.
 * Automatically applies the correct badge variant and capitalization.
 *
 * @example
 * ```tsx
 * <RoleBadge role="admin" />
 * <RoleBadge role="viewer" className="text-sm" />
 * ```
 */
export function RoleBadge({ role, className }: RoleBadgeProps) {
  const variant = getRoleBadgeVariant(role);
  const displayText = capitalize(role);

  return (
    <Badge variant={variant} className={cn('capitalize', className)}>
      {displayText}
    </Badge>
  );
}

